import type { CsvDownloadFacebook } from './csv-download-facebook.service'
import type { CsvDownloadInstagram } from './csv-download-instagram.service'
import type { CsvDownloadLine } from './csv-download-line.service'
import type { CsvDownloadTikTok } from './csv-download-tiktok.service'
import type { CsvDownloadTwitter } from './csv-download-twitter.service'
import type { CsvDownloadYoutube } from './csv-download-youtube.service'

import { app } from '../../app.module'

/**
 * 投稿のCSVダウンロード用の共通サービス
 * @param {Function} $q Promise用
 * @param {CsvDownloadFacebook} CsvDownloadFacebook CsvダウンロードFacebookサービス
 * @param {CsvDownloadTwitter} CsvDownloadTwitter CsvダウンロードTwitterサービス
 * @param {CsvDownloadInstagram} CsvDownloadInstagram CsvダウンロードInstagramサービス
 * @param {CsvDownloadYoutube} CsvDownloadYoutube CsvダウンロードYoutubeサービス
 * @param {CsvDownloadLine} CsvDownloadLine CsvダウンロードLineサービス
 * @param {CsvDownloadTikTok} CsvDownloadTikTok CsvダウンロードTikTokサービス
 * @return {Object} オブジェクト
 */
class CsvDownLoadPost {
  constructor(
    private $rootScope,
    private $q: ng.IQService,
    private PostService: any,
    private CsvDownloadFacebook: CsvDownloadFacebook,
    private CsvDownloadTwitter: CsvDownloadTwitter,
    private CsvDownloadInstagram: CsvDownloadInstagram,
    private CsvDownloadYoutube: CsvDownloadYoutube,
    private CsvDownloadLine: CsvDownloadLine,
    private CsvDownloadTikTok: CsvDownloadTikTok,
    private RestangularService
  ) {}

  /**
   * 投稿一覧に表示されている投稿をCSVとしてダウンロード
   * @param  {string} snsType 投稿タイプ
   * @param  {object} getOptions フィルタリングの要件
   * @return {ng.IPromise <string[][]>} 投稿一覧のCSVデータをPromiseとしてリターン
   */
  public download(snsType: string, getOptions): ng.IPromise<any> {
    const deferred = this.$q.defer()
    let csvData = []

    getOptions.limit = null
    getOptions.page = 0
    this.RestangularService.request('GET', '/post/list', {
      options: JSON.stringify(getOptions),
      snsType: snsType,
    }).then((result) => {
      const data = result[0].data

      if (data.length > 0) {
        switch (snsType) {
          case 'facebook':
            csvData = this.CsvDownloadFacebook.setPostCsv(data)
            break
          case 'twitter':
            csvData = this.CsvDownloadTwitter.setPostCsv(data)
            break
          case 'instagram':
            csvData = this.CsvDownloadInstagram.setPostCsv(data)
            break
          case 'youtube':
            csvData = this.CsvDownloadYoutube.setPostCsv(data)
            break
          case 'line':
            csvData = this.CsvDownloadLine.setPostCsv(data)
            break
          case 'tiktok':
            csvData = this.CsvDownloadTikTok.setPostCsv(data)
            break
        }
      }

      deferred.resolve(csvData)
    })

    return deferred.promise
  }
}

/**
 * アカウンントのCSVダウンロード用の共通サービス
 * @param {Function} $q Promise用
 * @param {CsvDownloadFacebook} CsvDownloadFacebook CsvダウンロードFacebookサービス
 * @param {CsvDownloadTwitter} CsvDownloadTwitter CsvダウンロードTwitterサービス
 * @param {CsvDownloadInstagram} CsvDownloadInstagram CsvダウンロードInstagramサービス
 * @param {CsvDownloadYoutube} CsvDownloadYoutube CsvダウンロードYoutubeサービス
 * @param {CsvDownloadLine} CsvDownloadLine CsvダウンロードLineサービス
 * @param {CsvDownloadTikTok} CsvDownloadTikTok CsvダウンロードTikTokサービス
 * @return {Object} オブジェクト
 */
class CsvDownloadAccount {
  constructor(
    private $rootScope,
    private $q: ng.IQService,
    private AccountDetailModalService: any,
    private AccountTargetService: any,
    private CsvDownloadFacebook: CsvDownloadFacebook,
    private CsvDownloadTwitter: CsvDownloadTwitter,
    private CsvDownloadInstagram: CsvDownloadInstagram,
    private CsvDownloadYoutube: CsvDownloadYoutube,
    private CsvDownloadLine: CsvDownloadLine,
    private CsvDownloadTikTok: CsvDownloadTikTok,
    private RestangularService
  ) {}

  /**
   * アカウントのCSVダウンロード
   * @param {string} snsType SNSタイプ
   * @param {any} accountInfo アカウント情報
   * @returns {ng.IPromise <string[][]>} CSVデータ
   */
  public download(
    snsType: string,
    accountInfo: {
      dataPerPost: boolean
      data: any
    }
  ): ng.IPromise<any> {
    const deferred = this.$q.defer()
    let csvData = []

    switch (snsType) {
      case 'facebook':
        csvData = this.CsvDownloadFacebook.setAccountCsv(accountInfo.data, accountInfo.dataPerPost)
        break
      case 'twitter':
        csvData = this.CsvDownloadTwitter.setAccountCsv(accountInfo.data, accountInfo.dataPerPost)
        break
      case 'instagram':
        csvData = this.CsvDownloadInstagram.setAccountCsv(accountInfo.data, accountInfo.dataPerPost)
        break
      case 'youtube':
        csvData = this.CsvDownloadYoutube.setAccountCsv(accountInfo.data, accountInfo.dataPerPost)
        break
      case 'line':
        csvData = this.CsvDownloadLine.setAccountCsv(accountInfo.data, accountInfo.dataPerPost)
        break
      case 'tiktok':
        csvData = this.CsvDownloadTikTok.setAccountCsv(accountInfo.data, accountInfo.dataPerPost)
        break
    }

    deferred.resolve(csvData)
    return deferred.promise
  }

  /**
   * アカウント詳細のグラフをCSVとしてダウンロード
   * @param  {string} graphType グラフタイプ
   * @param  {string} snsType SNSタイプ
   * @param  {boolean} dataPerObjectFlg 1投稿ごとフラグ(1アカウントごとフラグ)
   * @param {array} periodArray 期間リスト
   * @param  {object} data データ
   * @return {ng.IPromise <string[][]>} アカウント一覧のCSVデータをPromiseとしてリターン
   */
  public accountChangeDownload(
    graphType: string,
    snsType: string,
    dataPerObjectFlg: boolean,
    periodArray: {}[],
    data
  ): ng.IPromise<any> {
    const deferred = this.$q.defer()
    let csvData = []

    switch (snsType) {
      case 'facebook':
        csvData = this.CsvDownloadFacebook.setAccountChangeCsv(
          graphType,
          dataPerObjectFlg,
          periodArray,
          data
        )
        break
      case 'twitter':
        csvData = this.CsvDownloadTwitter.setAccountChangeCsv(
          graphType,
          dataPerObjectFlg,
          periodArray,
          data
        )
        break
      case 'instagram':
        csvData = this.CsvDownloadInstagram.setAccountChangeCsv(
          graphType,
          dataPerObjectFlg,
          periodArray,
          data
        )
        break
      case 'youtube':
        csvData = this.CsvDownloadYoutube.setAccountChangeCsv(
          graphType,
          dataPerObjectFlg,
          periodArray,
          data
        )
        break
      case 'line':
        csvData = this.CsvDownloadLine.setAccountChangeCsv(
          graphType,
          dataPerObjectFlg,
          periodArray,
          data
        )
        break
      case 'tiktok':
        csvData = this.CsvDownloadTikTok.setAccountChangeCsv(
          graphType,
          dataPerObjectFlg,
          periodArray,
          data
        )
        break
    }

    deferred.resolve(csvData)
    return deferred.promise
  }

  /**
   * アカウントフォロワー数のグラフをCSVとしてダウンロード
   * @param {array} periodArray 期間リスト
   * @param  {object} data データ
   * @return {ng.IPromise <string[][]>} アカウント一覧のCSVデータをPromiseとしてリターン
   */
  public accountFollowersDownload(periodArray: {}[], data): ng.IPromise<any> {
    const deferred = this.$q.defer()
    const csvData = []

    for (let key = 0; key < periodArray.length; key = key + 1) {
      if (key === 0) {
        csvData.push(['期間', 'フォロワー数'])
      }

      csvData.push([periodArray[key], data[key]])
    }

    deferred.resolve(csvData)

    return deferred.promise
  }

  /**
   * アカウントの投稿の対象データをCSVとしてダウンロード
   * @param  {string} analyticsTab 対象名
   * @param  {string} snsType SNSタイプ
   * @param  {boolean} dataPerPostFlg 1投稿ごとフラグ
   * @param  {array} datas データ
   * @return {ng.IPromise <string[][]>} アカウント一覧のCSVデータをPromiseとしてリターン
   */
  public accountTargetDownload(
    analyticsTab: string,
    snsType: string,
    dataPerPostFlg: boolean,
    datas
  ): ng.IPromise<any> {
    const deferred = this.$q.defer()
    let csvData = []
    const targetName = this.AccountTargetService.Data.setTargetTitle(analyticsTab, snsType)

    switch (snsType) {
      case 'facebook':
        csvData = this.CsvDownloadFacebook.setAccountTargetCsv(targetName, dataPerPostFlg, datas)
        break
      case 'twitter':
        csvData = this.CsvDownloadTwitter.setAccountTargetCsv(targetName, dataPerPostFlg, datas)
        break
      case 'instagram':
        csvData = this.CsvDownloadInstagram.setAccountTargetCsv(targetName, dataPerPostFlg, datas)
        break
      case 'youtube':
        csvData = this.CsvDownloadYoutube.setAccountTargetCsv(targetName, dataPerPostFlg, datas)
        break
      case 'line':
        csvData = this.CsvDownloadLine.setAccountTargetCsv(targetName, dataPerPostFlg, datas)
        break
      case 'tiktok':
        csvData = this.CsvDownloadTikTok.setAccountTargetCsv(targetName, dataPerPostFlg, datas)
        break
    }

    deferred.resolve(csvData)
    return deferred.promise
  }

  /**
   * アカウントの投稿の対象データをCSVとしてダウンロード
   * @param  {string} snsType SNSタイプ
   * @param  {boolean} dataPerPostFlg 1投稿ごとフラグ
   * @param  {object} getOptions 取得のオプション
   * @return {ng.IPromise <string[][]>} ハッシュタグ一覧のCSVデータをPromiseとしてリターン
   */
  public accountHashtagDownload(
    snsType: string,
    dataPerPostFlg: boolean,
    getOptions
  ): ng.IPromise<any> {
    const deferred = this.$q.defer()
    getOptions.limit = null

    // ハッシュタグを取得
    this.RestangularService.request('GET', '/analytics', {
      options: JSON.stringify(getOptions),
      snsType: snsType,
      target: 'hashtag',
    }).then((result) => {
      // CSVデータをセット
      this.accountTargetDownload('hashtag', snsType, dataPerPostFlg, result[0].data).then(
        (csvData) => {
          deferred.resolve(csvData)
        }
      )
    })

    return deferred.promise
  }
}

/**
 * CSVダウンロード用のサービスを1つにまとめ、オブジェクトとして格納
 * @param {Function} csvDownloadPosts 投稿のCSVダウンロード用
 * @param {Function} csvDownloadAccounts アカウントのCSVダウンロード用の共通サービス
 * @param {Function} csvDownloadTrendWords トレンドワードのCSVダウンロード用の共通サービス
 * @return {Object} サービスのオブジェクト
 */

function csvDownloadService(
  CsvDownLoadPost,
  CsvDownloadAccount,
  CsvDownloadTrendWord,
  CsvDownloadYahooWord,
  CsvDownloadBrand
) {
  return {
    Posts: CsvDownLoadPost,
    Accounts: CsvDownloadAccount,
    trendWords: CsvDownloadTrendWord,
    yahooWords: CsvDownloadYahooWord,
    Brands: CsvDownloadBrand,
  }
}

// CSVダウンロード用のサービスを定義
app
  .service('CsvDownLoadPost', CsvDownLoadPost)
  .service('CsvDownloadAccount', CsvDownloadAccount)
  .factory('csvDownloadService', csvDownloadService)
